import React from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import { Trash2, Inbox, Zap, Plus } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { useWorkflowStore } from '../store';
import { NodeActionPopover } from './NodeActionPopover';
import { AgentSummary } from '../api';

export function AgentNode({ id, data, selected, positionAbsoluteX, positionAbsoluteY }: NodeProps) {
  const { nodeLabel = 'New Step', agentName, hasError } = data as any;
  const isConfigured = Boolean(agentName && agentName !== "");
  
  const setNodes = useWorkflowStore(state => state.setNodes);
  const setEdges = useWorkflowStore(state => state.setEdges);
  const nodes = useWorkflowStore(state => state.nodes);
  const edges = useWorkflowStore(state => state.edges);
  const outfieldsMap = useWorkflowStore(state => state.outfieldsMap);
  
  const outfieldCount = outfieldsMap[agentName]?.length || 0;
  
  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation();
    setNodes(nodes.filter(n => n.id !== id));
    setEdges(edges.filter(e => e.source !== id && e.target !== id));
  };

  return (
    <div
      className={cn(
        "group relative flex flex-col rounded-[12px] bg-elevated text-left transition-all duration-200",
        selected ? "border-2 border-brand shadow-md" : "border border-line shadow-sm hover:border-brand/40",
        hasError && !selected ? "border-2 border-danger shadow-sm" : "",
      )}
      style={{ width: 280 }}
    >
      <Handle 
        type="target" 
        position={Position.Left} 
        className="w-1 h-1 opacity-0" 
      />
      
      {/* Header */}
      <div className="flex items-center justify-between px-3.5 py-3">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-brand border border-brand/30">
          <span className="h-1.5 w-1.5 rounded-full bg-brand"></span>
          {data.stepIndex ? `Step ${data.stepIndex}` : "Agent Step"}
        </span>
        <button 
          onClick={handleDelete}
          className="opacity-0 group-hover:opacity-100 text-ink-3 hover:text-danger transition-colors"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Body */}
      <div className="flex flex-col gap-1.5 px-3.5 pb-3">
        <div className="text-[13px] font-bold text-ink truncate">{nodeLabel}</div>
        <div className="flex items-center gap-1.5 text-[11px] font-medium text-ink-3">
          <Inbox className="h-3 w-3" />
          <span className={cn("truncate", !isConfigured && "italic")}>
            {isConfigured ? (data.agentLabel || agentName) : "No agent assigned"}
          </span>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-3.5 py-2.5 border-t border-line">
        <div className="flex items-center gap-1 text-[11px] font-bold text-brand">
          <Zap className="h-3 w-3 fill-brand text-brand" />
          <span>Active Outfields ({outfieldCount})</span>
        </div>
        <span className="text-[10px] font-bold tracking-wider text-ink-3 uppercase">Tools</span>
      </div>
      
      {hasError && (
        <div className="absolute -bottom-8 left-0 right-0 flex justify-center">
          <span className="bg-danger-soft text-danger text-xs px-2 py-1 rounded-full font-medium whitespace-nowrap shadow-sm border border-danger/30">
            Agent required
          </span>
        </div>
      )}

      {/* Custom Source Handle (+ Button) */}
      <NodeActionPopover 
        nodeId={id} 
        positionX={positionAbsoluteX || 0} 
        positionY={positionAbsoluteY || 0} 
        availableAgents={data.availableAgents as AgentSummary[]}
      />
    </div>
  );
}
